import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { publicApi } from "@/lib/publicApi";
import { useSiteSettings } from "@/lib/SiteSettingsContext";
import SEO from "@/components/public/SEO";
import { getLucideIcon } from "@/lib/iconMap";
import { CTA } from "@/lib/ctas";
import AuditReassurance from "@/components/public/AuditReassurance";
import Counter from "@/components/public/Counter";
import { fadeUp, fadeUpBold, staggerContainer, staggerChild, easeOutStrong, heroWordContainer, heroWord } from "@/lib/motion";

const HEADLINE_WORDS = [
    { t: "We", grad: false },
    { t: "build", grad: false },
    { t: "the", grad: false },
    { t: "systems", grad: true },
    { t: "your", grad: false },
    { t: "business", grad: false },
    { t: "runs", grad: false },
    { t: "on.", grad: false },
];

export default function Home() {
    const { settings } = useSiteSettings();
    const [services, setServices] = useState([]);
    const [cases, setCases] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const reduced = useReducedMotion();

    // Parallax for decorative background — subtle
    const { scrollY } = useScroll();
    const parallaxY = useTransform(scrollY, [0, 500], [0, -125]);

    useEffect(() => {
        Promise.all([
            publicApi.get("/services"),
            publicApi.get("/case-studies"),
            publicApi.get("/testimonials"),
        ])
            .then(([s, c, t]) => {
                setServices((s.data || []).slice(0, 4));
                const featured = (c.data || []).slice().sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
                setCases(featured.slice(0, 3));
                setTestimonials(t.data || []);
            })
            .catch(() => {});
    }, []);

    const stats = [
        { v: settings.hero_stat_1_value || "40+", l: settings.hero_stat_1_label || "Products shipped" },
        { v: settings.hero_stat_2_value || "6 wks", l: settings.hero_stat_2_label || "Avg. to launch" },
        { v: settings.hero_stat_3_value || "99.9%", l: settings.hero_stat_3_label || "Uptime" },
    ];
    const STEPS = [
        { n: "01", t: "Discover", p: "We map your goals, users, and constraints. You get a clear plan, scope, and price before anyone writes code." },
        { n: "02", t: "Design", p: "We design the journey — your site or app, the dashboards, the integrations — so the system works end to end, not just looks good." },
        { n: "03", t: "Build", p: "We ship weekly in working software. You see progress live, give feedback, and steer the direction in real time." },
        { n: "04", t: "Launch & support", p: "We launch with monitoring, analytics, and SEO live from day one. Then we stay on for support, fixes, and growth experiments." },
    ];
    const WHY = [
        { k: "A", t: "One team, end to end", p: "Strategy, design, and development under one roof. Fewer handoffs, faster shipping, one number to call." },
        { k: "B", t: "Built for small business reality", p: "From takeaways to startups — we ship systems that fit your budget, your team, and your customers." },
        { k: "C", t: "Engineered, not just designed", p: "Code you can audit, dashboards your team can run, performance you can prove. Not just a pretty marketing site." },
        { k: "D", t: "Long after launch", p: "SEO setup, analytics, WhatsApp ordering, payment integration — we wire it in and keep it healthy." },
    ];
    const featuredTestimonial = testimonials[0];

    return (
        <div data-testid="home-page" style={{ position: "relative", overflow: "hidden" }}>
            <SEO
                title="Omega IT Ltd — We build the systems your business runs on"
                description="Digital engineering studio shipping smart websites, custom apps, and connected business systems. Engineered to scale — simple enough for a takeaway, robust enough for a startup."
            />

            {/* Parallax decorative background glow */}
            {!reduced && (
                <motion.div
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        top: -100,
                        right: -100,
                        width: 500,
                        height: 500,
                        borderRadius: "50%",
                        background: "radial-gradient(circle, rgba(124,107,255,0.15) 0%, rgba(124,107,255,0) 70%)",
                        pointerEvents: "none",
                        y: parallaxY,
                        willChange: "transform",
                        zIndex: 0,
                    }}
                />
            )}

            {/* HERO */}
            <section className="px-5 sm:px-8 lg:px-12 pt-16 pb-20 lg:pt-24 lg:pb-28" style={{ position: "relative", zIndex: 1 }}>
                <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-16 items-center">
                    <div>
                        <motion.div
                            className="eyebrow"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, ease: easeOutStrong }}
                        >— DIGITAL ENGINEERING STUDIO</motion.div>
                        <motion.h1
                            className="font-display mt-5"
                            style={{ fontSize: "clamp(38px, 6.2vw, 68px)", lineHeight: 1.04, fontWeight: 700 }}
                            {...heroWordContainer}
                        >
                            {HEADLINE_WORDS.map((w, i) => (
                                <motion.span
                                    key={i}
                                    style={{ display: "inline-block", marginRight: "0.28em", willChange: "transform, opacity" }}
                                    {...heroWord(w.grad)}
                                >
                                    {w.grad ? <span className="grad">{w.t}</span> : w.t}
                                </motion.span>
                            ))}
                        </motion.h1>
                        <motion.p
                            className="mt-6"
                            style={{ fontSize: "17px", lineHeight: 1.6, maxWidth: "32em" }}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.7, ease: easeOutStrong }}
                        >
                            Omega IT designs and ships smart websites, custom apps, and the digital systems that connect them — engineered to scale, simple enough for a takeaway, robust enough for a startup. Measured on real business results.
                        </motion.p>
                        <motion.div
                            className="mt-8 flex flex-wrap gap-3"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.9, ease: easeOutStrong }}
                        >
                            <motion.div whileHover={{ y: -2, boxShadow: "0 20px 45px -12px rgba(124, 107, 255, 0.55)" }} whileTap={{ scale: 0.97 }} transition={{ duration: 0.2 }}>
                                <Link to={CTA.AUDIT.href} className="btn-primary" data-testid="hero-primary-cta">{CTA.AUDIT.label} →</Link>
                            </motion.div>
                            <motion.a
                                href="#work"
                                onClick={(e) => { e.preventDefault(); document.getElementById("work")?.scrollIntoView({ behavior: "smooth" }); }}
                                className="btn-ghost"
                                data-testid="hero-secondary-cta"
                                whileHover={{ y: -2 }}
                                whileTap={{ scale: 0.97 }}
                                transition={{ duration: 0.2 }}
                            >{CTA.SEE_WORK.label}</motion.a>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: 1.0 }}
                        >
                            <AuditReassurance />
                        </motion.div>
                        <motion.div
                            className="stat-row"
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 1.05, ease: easeOutStrong }}
                        >
                            {stats.map((s, i) => (
                                <div key={i} className="stat" data-testid={`hero-stat-${i + 1}`}>
                                    <div className="v"><Counter value={s.v} /></div>
                                    <div className="l">{s.l}</div>
                                </div>
                            ))}
                        </motion.div>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.0, delay: 0.3, ease: easeOutStrong }}
                    >
                        <div className="schematic" aria-hidden="true">
                            <div className="grid-bg" />
                            <svg viewBox="0 0 400 400" width="100%" height="100%">
                                <defs>
                                    <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
                                        <stop offset="0%" stopColor="#7C6BFF" stopOpacity="0.45" />
                                        <stop offset="60%" stopColor="#7C6BFF" stopOpacity="0.08" />
                                        <stop offset="100%" stopColor="#7C6BFF" stopOpacity="0" />
                                    </radialGradient>
                                </defs>
                                <circle cx="200" cy="200" r="120" fill="url(#centerGlow)" />
                                <g stroke="rgba(124,107,255,0.45)" strokeWidth="1.2" strokeDasharray="3 5" fill="none" className="pulse">
                                    <line x1="200" y1="200" x2="90" y2="90" />
                                    <line x1="200" y1="200" x2="310" y2="90" />
                                    <line x1="200" y1="200" x2="90" y2="310" />
                                    <line x1="200" y1="200" x2="310" y2="310" />
                                </g>
                                <circle cx="200" cy="200" r="46" fill="#152441" stroke="rgba(124,107,255,0.6)" strokeWidth="1.5" />
                                <text x="200" y="215" textAnchor="middle" fontFamily="Clash Display, sans-serif" fontSize="38" fontWeight="700" fill="#EDF0F8">Ω</text>
                                <motion.g animate={reduced ? {} : { y: [0, -8, 0] }} transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut" }}>
                                    <circle cx="90" cy="90" r="26" fill="#0D162B" stroke="#7C6BFF" strokeWidth="1.4" />
                                    <text x="90" y="95" textAnchor="middle" fontFamily="Clash Display, sans-serif" fontSize="11" fontWeight="600" fill="#9C8CFF" letterSpacing="1.6">WEB</text>
                                </motion.g>
                                <motion.g animate={reduced ? {} : { y: [0, -10, 0] }} transition={{ duration: 4.6, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}>
                                    <circle cx="310" cy="90" r="26" fill="#0D162B" stroke="#2DE2C6" strokeWidth="1.4" />
                                    <text x="310" y="95" textAnchor="middle" fontFamily="Clash Display, sans-serif" fontSize="11" fontWeight="600" fill="#2DE2C6" letterSpacing="1.6">APPS</text>
                                </motion.g>
                                <motion.g animate={reduced ? {} : { y: [0, 8, 0] }} transition={{ duration: 5.0, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}>
                                    <circle cx="90" cy="310" r="26" fill="#0D162B" stroke="#2DE2C6" strokeWidth="1.4" />
                                    <text x="90" y="315" textAnchor="middle" fontFamily="Clash Display, sans-serif" fontSize="11" fontWeight="600" fill="#2DE2C6" letterSpacing="1.6">DATA</text>
                                </motion.g>
                                <motion.g animate={reduced ? {} : { y: [0, 10, 0] }} transition={{ duration: 4.4, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}>
                                    <circle cx="310" cy="310" r="26" fill="#0D162B" stroke="#7C6BFF" strokeWidth="1.4" />
                                    <text x="310" y="315" textAnchor="middle" fontFamily="Clash Display, sans-serif" fontSize="11" fontWeight="600" fill="#9C8CFF" letterSpacing="1.6">AUTO</text>
                                </motion.g>
                            </svg>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* SERVICES */}
            <motion.section id="services" className="px-5 sm:px-8 lg:px-12 py-20" {...fadeUpBold} style={{ position: "relative", zIndex: 1 }}>
                <div className="max-w-7xl mx-auto">
                    <div className="max-w-2xl">
                        <div className="eyebrow">— WHAT WE DO</div>
                        <h2 className="font-display mt-3" style={{ fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 700, lineHeight: 1.1 }}>Four modules. One connected system.</h2>
                        <p className="mt-4" style={{ fontSize: "16.5px", lineHeight: 1.6 }}>
                            Most agencies hand you a website and walk away. We build each piece to plug into the next — so your site, your app, your data, and your workflows actually talk to each other.
                        </p>
                    </div>
                    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {services.map((s, i) => {
                            const Icon = getLucideIcon(s.icon);
                            return (
                                <motion.div
                                    key={s.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.15 }}
                                    transition={{ duration: 0.6, delay: i * 0.08, ease: easeOutStrong }}
                                >
                                    <Link to={`/services/${s.slug}`} className="module" data-testid={`home-module-${s.slug}`}>
                                        <span className="ico"><Icon className="w-4.5 h-4.5" /></span>
                                        <div className="mtag mt-5">{String(i + 1).padStart(2, "0")} / {s.title.split(" ")[0]}</div>
                                        <h3>{s.title}</h3>
                                        <p style={{ fontSize: "14.5px", marginTop: "10px", lineHeight: 1.55 }}>{s.short_description}</p>
                                        <ul className="flist">
                                            {(s.features || []).slice(0, 4).map((f, k) => (<li key={k}>{f}</li>))}
                                        </ul>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </motion.section>

            {/* PROCESS */}
            <motion.section id="process" className="px-5 sm:px-8 lg:px-12 py-20" {...fadeUpBold} style={{ position: "relative", zIndex: 1 }}>
                <div className="max-w-7xl mx-auto">
                    <div className="max-w-2xl">
                        <div className="eyebrow">— HOW WE WORK</div>
                        <h2 className="font-display mt-3" style={{ fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 700 }}>A clear path from idea to live system.</h2>
                        <p className="mt-4" style={{ fontSize: "16.5px" }}>No black boxes. You see progress every week, in working software — not slide decks.</p>
                    </div>
                    <motion.div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" {...staggerContainer}>
                        {STEPS.map((s) => (
                            <motion.div key={s.n} className="step" {...staggerChild}>
                                <div className="num">{s.n}</div>
                                <div className="bar" />
                                <h4>{s.t}</h4>
                                <p>{s.p}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </motion.section>

            {/* WORK */}
            <motion.section id="work" className="px-5 sm:px-8 lg:px-12 py-20" {...fadeUpBold} style={{ position: "relative", zIndex: 1 }}>
                <div className="max-w-7xl mx-auto">
                    <div className="max-w-2xl">
                        <div className="eyebrow">— SELECTED WORK</div>
                        <h2 className="font-display mt-3" style={{ fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 700 }}>Systems we've shipped.</h2>
                        <p className="mt-4" style={{ fontSize: "16.5px" }}>Real projects, real outcomes — from local businesses to scaling startups.</p>
                    </div>
                    <motion.div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" {...staggerContainer}>
                        {cases.map((c, i) => (
                            <motion.div key={c.id} {...staggerChild}>
                                <Link to={`/case-studies/${c.slug}`} className="card" data-testid={`home-case-${c.slug}`}>
                                    <div className="thumb">
                                        {c.images && c.images[0] ? (
                                            <img src={c.images[0]} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <CaseStudyArt index={i} />
                                        )}
                                    </div>
                                    <div className="body">
                                        <div className="ctag">{c.client_business_type || "Project"}</div>
                                        <h4>{c.title}</h4>
                                        <p style={{ fontSize: "14.5px", lineHeight: 1.55 }}>{(c.solution || c.problem || "").slice(0, 130)}…</p>
                                        <div className="result">View case study →</div>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </motion.section>

            {/* WHY US */}
            <motion.section id="why" className="px-5 sm:px-8 lg:px-12 py-20" {...fadeUpBold} style={{ position: "relative", zIndex: 1 }}>
                <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
                    <div>
                        <div className="eyebrow">— WHY OMEGA</div>
                        <h2 className="font-display mt-3 mb-8" style={{ fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 700, lineHeight: 1.1 }}>Built like engineers, not just designers.</h2>
                        <motion.div {...staggerContainer}>
                            {WHY.map((w) => (
                                <motion.div key={w.k} className="why-item" {...staggerChild}>
                                    <div className="badge">{w.k}</div>
                                    <div>
                                        <h4>{w.t}</h4>
                                        <p>{w.p}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                    {featuredTestimonial ? (
                        <motion.div className="quote-card" {...fadeUp}>
                            <div className="eyebrow mb-4">— CLIENT</div>
                            <p className="q">"<span className="grad">{featuredTestimonial.review_text.split(".")[0]}.</span>{featuredTestimonial.review_text.slice(featuredTestimonial.review_text.split(".")[0].length + 1)}"</p>
                            <div className="mt-6" style={{ fontSize: "14px", color: "var(--muted)" }}>
                                By <span style={{ color: "var(--text)", fontWeight: 600 }}>{featuredTestimonial.client_name}</span> · {featuredTestimonial.business_name}
                            </div>
                        </motion.div>
                    ) : (
                        <motion.div
                            className="quote-card"
                            data-testid="home-why-fallback"
                            {...fadeUp}
                        >
                            <div className="eyebrow mb-4">— WHY OMEGA</div>
                            <ul style={{ display: "flex", flexDirection: "column", gap: 18, listStyle: "none", padding: 0, margin: 0 }}>
                                {[
                                    "One team, from strategy to launch — no handoffs, no waiting on three suppliers to talk to each other.",
                                    "Priced up front, no surprises — you get a fixed quote before we start work, in writing, within two working days.",
                                    "Built to run without us — you own the code, the content, the customer data. We stay on if you want, not because you have to.",
                                ].map((b, i) => (
                                    <li key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                                        <span
                                            style={{
                                                flex: "0 0 auto",
                                                width: 28, height: 28, borderRadius: "50%",
                                                background: "linear-gradient(135deg,#7C6BFF,#2DE2C6)",
                                                color: "#0D162B",
                                                display: "grid", placeItems: "center",
                                                fontFamily: "Clash Display, sans-serif",
                                                fontWeight: 700, fontSize: 13,
                                            }}
                                        >{String(i + 1).padStart(2, "0")}</span>
                                        <span style={{ fontSize: 15, lineHeight: 1.55, color: "var(--text)" }}>{b}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    )}
                </div>
            </motion.section>

            {/* CTA BAND */}
            <motion.section className="px-5 sm:px-8 lg:px-12 py-16" {...fadeUpBold} style={{ position: "relative", zIndex: 1 }}>
                <div className="max-w-6xl mx-auto cta-band">
                    <div className="eyebrow">— LET'S TALK</div>
                    <h2 className="font-display mt-3 mx-auto" style={{ fontSize: "clamp(28px, 4.5vw, 44px)", fontWeight: 700, maxWidth: "18em" }}>Ready to build the last system you'll need?</h2>
                    <p className="mt-5 mx-auto" style={{ fontSize: "16.5px", maxWidth: "640px", lineHeight: 1.6 }}>
                        Tell us what you're trying to ship. We'll come back with a clear plan, a timeline, and a fixed price — usually within two working days.
                    </p>
                    <div style={{ marginTop: 30, display: "inline-flex", flexDirection: "column", alignItems: "center" }}>
                        <motion.div whileHover={{ y: -2, boxShadow: "0 20px 45px -12px rgba(124, 107, 255, 0.55)" }} whileTap={{ scale: 0.97 }} transition={{ duration: 0.2 }}>
                            <Link to={CTA.AUDIT.href} className="btn-primary" data-testid="home-cta-band-primary">{CTA.AUDIT.label} →</Link>
                        </motion.div>
                        <AuditReassurance align="center" />
                    </div>
                </div>
            </motion.section>
        </div>
    );
}

function CaseStudyArt({ index }) {
    if (index === 0) {
        return (
            <svg viewBox="0 0 400 250" width="100%" height="100%">
                <defs><linearGradient id="g0" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#7C6BFF" /><stop offset="100%" stopColor="#2DE2C6" /></linearGradient></defs>
                <rect width="400" height="250" fill="#0c1430" />
                <circle cx="120" cy="125" r="70" fill="none" stroke="url(#g0)" strokeWidth="1.5" opacity="0.6" />
                <circle cx="180" cy="125" r="50" fill="none" stroke="#7C6BFF" strokeWidth="1" opacity="0.4" />
                <rect x="240" y="80" width="100" height="90" rx="10" fill="#152441" stroke="#7C6BFF" strokeWidth="1" opacity="0.5" />
                <line x1="50" y1="200" x2="350" y2="200" stroke="#2DE2C6" strokeWidth="1" strokeDasharray="3 4" opacity="0.4" />
            </svg>
        );
    }
    if (index === 1) {
        return (
            <svg viewBox="0 0 400 250" width="100%" height="100%">
                <rect width="400" height="250" fill="#0c1430" />
                {[0,1,2,3,4,5].map((i) => (<rect key={i} x={40 + i * 55} y={70 + (i % 2) * 30} width="40" height="80" rx="6" fill="#152441" stroke="#7C6BFF" opacity={0.4 + i * 0.08} />))}
                <circle cx="200" cy="200" r="30" fill="none" stroke="#2DE2C6" strokeWidth="1.5" opacity="0.7" />
            </svg>
        );
    }
    return (
        <svg viewBox="0 0 400 250" width="100%" height="100%">
            <rect width="400" height="250" fill="#0c1430" />
            <polygon points="200,40 340,210 60,210" fill="none" stroke="#7C6BFF" strokeWidth="1.5" opacity="0.55" />
            <polygon points="200,90 290,200 110,200" fill="none" stroke="#2DE2C6" strokeWidth="1" opacity="0.45" />
            <circle cx="200" cy="170" r="14" fill="#7C6BFF" opacity="0.8" />
        </svg>
    );
}
